"use client";

/** The general contact form on /sales/ and /support/: shared contact fields,
 * one topic dropdown, one message box. Parameterized by topic list, recipient
 * and per-topic hints so both pages stay a single implementation.
 *
 * Commercial pages can deep-link with a preset topic (`/sales/?topic=reseller`)
 * — read client-side so the pages stay statically rendered. Callers must wrap
 * this component in <Suspense> (useSearchParams requires it on static pages). */

import { useSearchParams } from "next/navigation";
import { useMemo, useState } from "react";
import Alert from "react-bootstrap/Alert";
import Form from "react-bootstrap/Form";

import { MAX_TEXT_FIELD, Option, validateContact } from "@/lib/contact";
import { NewsletterKind } from "@/lib/newsletter";
import {
  BaseFields,
  BaseFormState,
  EMPTY_BASE,
  Honeypot,
  InquiryErrors,
  SelectGroup,
  SubmitButton,
  useInquiryForm,
} from "./InquiryForm";
import { NewsletterChoices } from "./NewsletterChoices";
import { PrivacyNotice } from "./PrivacyNotice";

interface ContactFormState extends BaseFormState {
  topic: string;
  message: string;
  newsletters: NewsletterKind[];
}

export interface ContactFormProps {
  submitUrl: string;
  // Direct email address, used in failure messages as the escape hatch.
  address: string;
  topics: Option[];
  companyRequired?: boolean;
  includeWebsite?: boolean;
  // Per-topic inline hint (an <Alert>), shown when that topic is selected.
  hints?: Record<string, React.ReactNode>;
  // Rendered inside the success alert once the message is sent.
  done: React.ReactNode;
  submitLabel?: string;
  newsletter: NewsletterKind;
}

export default function ContactForm({
  submitUrl,
  address,
  topics,
  companyRequired = true,
  includeWebsite = true,
  hints = {},
  done,
  submitLabel = "Send message",
  newsletter,
}: ContactFormProps) {
  const params = useSearchParams();
  // Preset topic from the URL, validated against the known options; unknown
  // values are ignored.
  const presetTopic = useMemo(() => {
    const topic = params.get("topic");
    return topic && topics.some((o) => o.value === topic) ? topic : null;
  }, [params, topics]);
  const [topicTouched, setTopicTouched] = useState(false);

  const form = useInquiryForm<ContactFormState>({
    initial: { ...EMPTY_BASE, topic: presetTopic ?? "", message: "", newsletters: [] },
    submitUrl,
    fallback: address,
    validate: (payload) => validateContact(payload, topics, { companyRequired }),
  });

  if (form.done) {
    return <Alert variant="secondary">{done}</Alert>;
  }

  // Suppress the hint while the topic still sits on the URL preset — the page
  // that set that link already gave the context, and steering someone back to
  // where they just came from is circular.
  const hintVisible =
    form.state.topic && (topicTouched || form.state.topic !== presetTopic);
  const hint = hintVisible ? hints[form.state.topic] : null;

  return (
    <Form onSubmit={form.onSubmit} noValidate>
      <InquiryErrors errors={form.errors} />
      <BaseFields
        state={form.state}
        set={form.set}
        companyRequired={companyRequired}
        includeWebsite={includeWebsite}
      />
      <SelectGroup
        label="What is this about?"
        id="topic"
        options={topics}
        value={form.state.topic}
        onChange={(value) => {
          setTopicTouched(true);
          form.set("topic", value);
        }}
      />
      {hint}
      <Form.Group className="mb-4">
        <Form.Label htmlFor="message" className="fw-bold">Your message</Form.Label>
        <Form.Control
          id="message"
          as="textarea"
          rows={6}
          value={form.state.message}
          onChange={(e) => form.set("message", e.target.value)}
          maxLength={MAX_TEXT_FIELD}
          required
        />
      </Form.Group>
      <NewsletterChoices
        value={form.state.newsletters}
        onChange={(value) => form.set("newsletters", value)}
        kinds={[newsletter]}
        compact
      />
      <Honeypot value={form.state.phone} onChange={(value) => form.set("phone", value)} />
      <SubmitButton submitting={form.submitting} label={submitLabel} />
      <PrivacyNotice subject="details" />
    </Form>
  );
}
